import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Download, Calendar, Loader2, Car, Star, Mail, CreditCard, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toPublicStorageUrl } from '../lib/url';
import { DEFAULT_DOCUMENT_ACCENT } from '../lib/brandAccent';
import { CHART_TOKENS, readToken } from '../lib/themeTokens';

interface InvoiceDetails {
    invoice_id: string;
    invoice_number: string;
    status: string;
    total_gross: number;
    vat_amount: number;
    amount_paid: number;
    balance_due: number;
    customer_name: string;
    vehicle_reg: string;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_year: number;
    mot_expiry: string | null;
    garage_name: string;
    garage_branding: Record<string, any>;
    thank_you_message: string | null;
    google_review_url: string | null;
    garage_phone: string | null;
    garage_email: string | null;
    pdf_storage_path: string | null;
    pdf_url?: string;
    payment_reference: string | null;
}

// ── Confetti Canvas ──
function ConfettiCanvas() {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        // A canvas cannot read CSS variables; resolve the theme's chart palette now.
        const colors = CHART_TOKENS.slice(0, 6).map((t) => readToken(t));
        const pieces: Array<{
            x: number; y: number; w: number; h: number;
            color: string; vx: number; vy: number; rot: number; vr: number;
            opacity: number;
        }> = [];

        for (let i = 0; i < 80; i++) {
            pieces.push({
                x: Math.random() * canvas.width,
                y: -Math.random() * canvas.height * 0.5,
                w: Math.random() * 8 + 4,
                h: Math.random() * 6 + 2,
                color: colors[Math.floor(Math.random() * colors.length)],
                vx: (Math.random() - 0.5) * 3,
                vy: Math.random() * 3 + 2,
                rot: Math.random() * Math.PI * 2,
                vr: (Math.random() - 0.5) * 0.2,
                opacity: 1,
            });
        }

        let frame = 0;
        const maxFrames = 180; // 3 seconds at 60fps

        const animate = () => {
            frame++;
            if (frame > maxFrames) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const fadeStart = maxFrames * 0.6;
            pieces.forEach(p => {
                p.x += p.vx;
                p.y += p.vy;
                p.vy += 0.05; // gravity
                p.rot += p.vr;
                if (frame > fadeStart) {
                    p.opacity = Math.max(0, 1 - (frame - fadeStart) / (maxFrames - fadeStart));
                }

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rot);
                ctx.globalAlpha = p.opacity;
                ctx.fillStyle = p.color;
                ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                ctx.restore();
            });

            requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-50"
            style={{ width: '100vw', height: '100vh' }}
        />
    );
}

export default function PaymentSuccess() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [invoice, setInvoice] = useState<InvoiceDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showConfetti, setShowConfetti] = useState(true);

    useEffect(() => {
        // Stop confetti after 3 seconds
        const timer = setTimeout(() => setShowConfetti(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!token) {
            setError('No invoice reference found.');
            setLoading(false);
            return;
        }

        const fetchDetails = async () => {
            try {
                // Payment redirects retain the same expiring, revocable capability.
                const { data: res, error: fnError } = await supabase.functions.invoke(
                    `get-public-invoice?token=${encodeURIComponent(token)}`,
                    { method: 'GET' },
                );

                if (fnError) throw fnError;

                const data = res?.data;
                if (!data || !['paid', 'partially_paid'].includes(data.status)) {
                    setError('processing');
                    setLoading(false);
                    return;
                }

                // `data.pdf_url` already carries a short-lived signed URL: the
                // edge function mints it with the service role in success mode.
                // Signing cannot happen in the browser — as `anon`, storage RLS
                // denies createSignedUrl on the private `invoices-issued`
                // bucket, which is why the download silently never worked
                // before.

                if (data?.pdf_url) data.pdf_url = toPublicStorageUrl(data.pdf_url);
                setInvoice(data);
            } catch (err) {
                console.error('Failed to load invoice details:', err);
                setError('processing');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [token]);

    const firstName = invoice?.customer_name?.split(' ')[0] || 'there';

    const formatMOT = (dateStr: string | null) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const accentColor = invoice?.garage_branding?.invoice_accent_color || invoice?.garage_branding?.accent_color || DEFAULT_DOCUMENT_ACCENT;
    const logoUrl = invoice?.garage_branding?.logo_url;

    // Truncate payment ref for display (pi_XXXX...XXXX)
    const shortRef = invoice?.payment_reference
        ? `${invoice.payment_reference.slice(0, 7)}...${invoice.payment_reference.slice(-4)}`
        : null;

    if (loading) {
        return (
            <div className="min-h-screen bg-panel flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-success mx-auto mb-4" />
                    <p className="text-fg-3 text-sm">Loading your receipt...</p>
                </div>
            </div>
        );
    }

    if (error === 'processing') {
        return (
            <div className="min-h-screen bg-panel flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-tint/5 backdrop-blur-xl border border-tint/10 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gold-fill/10 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-gold animate-spin" />
                    </div>
                    <h1 className="text-2xl font-bold text-fg mb-2">Payment Processing</h1>
                    <p className="text-fg-3 mb-6">
                        Your payment is being confirmed. This usually takes a few seconds.
                        Please refresh this page shortly.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-tint/10 hover:bg-tint/15 text-fg rounded-xl font-medium transition-all duration-200"
                    >
                        Refresh Page
                    </button>
                </div>
            </div>
        );
    }

    if (error || !invoice) {
        return (
            <div className="min-h-screen bg-panel flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-tint/5 backdrop-blur-xl border border-tint/10 rounded-2xl p-8 text-center">
                    <h1 className="text-2xl font-bold text-fg mb-2">Something went wrong</h1>
                    <p className="text-fg-3">{error || 'Unable to load invoice details.'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-panel flex items-center justify-center p-4 md:p-6">
            {showConfetti && <ConfettiCanvas />}

            <div className="max-w-lg w-full">
                {/* Success Card */}
                <div className="bg-tint/5 backdrop-blur-xl border border-tint/10 rounded-2xl overflow-hidden">

                    {/* Header with Logo + Checkmark */}
                    <div
                        className="px-8 pt-10 pb-8 text-center"
                        style={{
                            background: `linear-gradient(135deg, ${accentColor}15 0%, transparent 50%)`,
                        }}
                    >
                        {/* Garage Logo */}
                        {logoUrl && (
                            <div className="mb-5">
                                <img
                                    src={logoUrl}
                                    alt={invoice.garage_name}
                                    data-theme="paper" className="h-12 w-auto mx-auto object-contain bg-card rounded-lg p-1.5"
                                />
                            </div>
                        )}

                        {/* Animated Checkmark */}
                        <div
                            className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center animate-[bounceIn_0.6s_ease-out]"
                            style={{ backgroundColor: `${accentColor}20` }}
                        >
                            <CheckCircle
                                className="w-10 h-10"
                                style={{ color: accentColor }}
                            />
                        </div>

                        <h1 className="text-2xl md:text-3xl font-bold text-fg mb-1">
                            Payment Received, {firstName}!
                        </h1>
                        <p className="text-fg-3 text-sm">
                            Invoice #{invoice.invoice_number}
                        </p>
                    </div>

                    {/* Amount */}
                    <div className="px-8 py-6 border-t border-tint/5">
                        <div className="flex items-center justify-between">
                            <span className="text-fg-3 text-sm">Amount Paid</span>
                            <span
                                className="text-2xl font-bold font-mono tabular-nums"
                                style={{ color: accentColor }}
                            >
                                £{Number(invoice.amount_paid).toFixed(2)}
                            </span>
                        </div>
                        {invoice.vat_amount > 0 && (
                            <p className="text-right text-xs text-fg-4 mt-1">
                                inc. £{Number(invoice.vat_amount).toFixed(2)} VAT
                            </p>
                        )}
                    </div>

                    {/* Payment Reference */}
                    {shortRef && (
                        <div className="px-8 py-4 border-t border-tint/5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-tint/5 flex items-center justify-center flex-shrink-0">
                                    <CreditCard className="w-5 h-5 text-fg-3" />
                                </div>
                                <div>
                                    <p className="text-fg-4 text-xs">Payment Reference</p>
                                    <p className="text-fg text-sm font-mono">{shortRef}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Vehicle Info */}
                    {invoice.vehicle_reg && (
                        <div className="px-8 py-5 border-t border-tint/5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-tint/5 flex items-center justify-center flex-shrink-0">
                                    <Car className="w-5 h-5 text-fg-3" />
                                </div>
                                <div>
                                    <p className="text-fg font-medium">
                                        {invoice.vehicle_year && `${invoice.vehicle_year} `}
                                        {invoice.vehicle_make} {invoice.vehicle_model}
                                    </p>
                                    <p className="text-fg-4 text-sm font-mono">{invoice.vehicle_reg}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MOT Reminder */}
                    {invoice.mot_expiry && (
                        <div className="px-8 py-5 border-t border-tint/5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center flex-shrink-0">
                                    <Calendar className="w-5 h-5 text-info" />
                                </div>
                                <div>
                                    <p className="text-fg text-sm font-medium">MOT Due</p>
                                    <p className="text-fg-3 text-sm">
                                        {formatMOT(invoice.mot_expiry)} — we'll send you a reminder.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Garage & Thank You */}
                    <div className="px-8 py-6 border-t border-tint/5">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-tint/5 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-5 h-5 text-fg-3" />
                            </div>
                            <p className="text-fg font-medium">
                                Thank you for choosing {invoice.garage_name}
                            </p>
                        </div>
                        {invoice.thank_you_message && (
                            <p className="text-fg-3 text-sm italic ml-[52px]">
                                "{invoice.thank_you_message}"
                            </p>
                        )}
                    </div>

                    {/* Email Confirmation */}
                    <div className="px-8 py-4 border-t border-tint/5">
                        <div className="flex items-center gap-2 text-fg-4 text-sm">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span>A receipt has been emailed to you</span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="px-8 py-6 border-t border-tint/5 space-y-3">
                        {/* Download PDF */}
                        {invoice.pdf_url && (
                            <a
                                href={invoice.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-on-fill transition-all duration-200 hover:opacity-90"
                                style={{ backgroundColor: accentColor }}
                            >
                                <Download className="w-5 h-5" />
                                Download Paid Invoice
                            </a>
                        )}

                        {/* Google Review CTA */}
                        {invoice.google_review_url && (
                            <a
                                href={invoice.google_review_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gold bg-gold-fill/10 border border-gold/20 transition-all duration-200 hover:bg-gold-fill/20"
                            >
                                <Star className="w-5 h-5" />
                                Leave Us a Review ⭐
                            </a>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-fg-4 text-xs mt-6">
                    Powered by ForgeDash
                </p>
            </div>

            {/* Bounce-in animation */}
            <style>{`
        @keyframes bounceIn {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
        </div>
    );
}
